import {
  AppComponent,
  BuilderComponent,
  CompRef,
  DocumentServicesObject,
  PossibleViewModes,
} from '@wix/document-services-types';
import {
  getTpaPageIdByWidgetCompRef,
  isComponentEssential,
} from './unifiedComponents/componentSettings';
import {
  AppData,
  ComponentType,
  UnifiedWidget,
} from '../../types/unifiedComponents';
import {
  ExtendedPlatformContext,
  PlatformContext,
} from '../../types/platformApi';
import { IExperimentsApiAdapter } from '../utils/experimentsApiAdapter';
import {
  addBuilderComponent as _addBuilderComponent,
  applyPresets,
} from './unifiedComponents/installation/addComponents/addBuilderComponent';
import { ManifestComponentExtensionType } from '@wix/editor-platform-manifest-services';
import { ComponentTypes } from '@wix/editor-const';
import { TPAComponentType } from '@wix/editor-platform-sdk-types';
import { addCustomElement } from './unifiedComponents/installation/addComponents/addCustomElement';
import { EditorPointer } from '@wix/editor-document-logic-api';

const appWidget = 'platform.components.AppWidget';
const appController = 'platform.components.AppController';

const getAppFromController = (
  documentServices: DocumentServicesObject,
  controllerRef: CompRef,
) => {
  const { applicationId } =
    documentServices.components.data.get(controllerRef) || {};
  const { applicationId: actualApplicationId } =
    documentServices.tpa.app.getDataByAppDefId(applicationId) || {};
  return { applicationId: actualApplicationId, appDefinitionId: applicationId };
};

const getAppFromTPA = (
  documentServices: DocumentServicesObject,
  compRef: CompRef,
) => {
  const { applicationId, appDefinitionId } =
    documentServices.components.data.get(compRef) || {};
  return { applicationId, appDefinitionId };
};

export const getComponentApp = (
  documentServices: DocumentServicesObject,
  compRef: CompRef,
): { appDefinitionId?: string; applicationId?: number } => {
  const compType = documentServices.components.getType(compRef);
  if (compType === appWidget || compType === appController) {
    return getAppFromController(documentServices, compRef);
  }
  const { controllerRef } =
    documentServices.platform.controllers.connections.getPrimaryConnection(
      compRef,
    ) || {};
  if (controllerRef) {
    return getAppFromController(documentServices, controllerRef as CompRef);
  } else {
    return getAppFromTPA(documentServices, compRef);
  }
};

type EssentialCompAppData = {
  isEssential: boolean;
  appData?: AppData;
  essentialChildren?: { compRef: CompRef; appData: AppData }[];
};

type AppDataWithComponentsEssentiality = {
  [appDefId: string]: AppData & {
    essentialsComponents: CompRef[];
    nonEssentialsComponents: CompRef[];
  };
};

const getCompAppData = (
  documentServices: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  compRef: CompRef,
): EssentialCompAppData | undefined => {
  const compData = documentServices.components.data.get(compRef);
  const appDefId: string = compData?.appDefinitionId || compData?.applicationId;

  if (appDefId) {
    const appData = documentServices.platform.getAppDataByAppDefId(
      appDefId,
    ) as AppData;
    const tpaPageId = getTpaPageIdByWidgetCompRef(documentServices, compRef);

    const shouldReadDataFromNewSchema = experimentsAPI.enabled(
      'specs.editor-platform.unified-components-proto-changes',
    );

    return {
      isEssential: isComponentEssential(
        compData.widgetId,
        appData,
        shouldReadDataFromNewSchema,
        tpaPageId,
      ),
      appData,
    };
  }

  return undefined;
};

const shouldClassifyAsEssential = (
  documentServices: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  compRef: CompRef,
): EssentialCompAppData => {
  const compAppData = getCompAppData(documentServices, experimentsAPI, compRef);
  if (compAppData) {
    return compAppData;
  }

  const essentialChildren = documentServices.components
    .getChildren(compRef)
    .reduce((acc, childRef) => {
      const childClassification = getCompAppData(
        documentServices,
        experimentsAPI,
        childRef,
      );
      if (childClassification?.isEssential) {
        acc.push({
          compRef: childRef,
          appData: childClassification.appData as AppData,
        });
      }
      return acc;
    }, [] as { compRef: CompRef; appData: AppData }[]);

  return {
    isEssential: false,
    essentialChildren,
  };
};

const addAppKeyIfNeeded = (
  dict: AppDataWithComponentsEssentiality,
  appDefId: string,
  appData?: AppData,
) => {
  if (!dict[appDefId]) {
    dict[appDefId] = {
      ...(appData as AppData),
      essentialsComponents: [],
      nonEssentialsComponents: [],
    };
  }
};

const getComponentsAppsDataWithEssentiality = (
  documentServices: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  components: CompRef[],
): AppDataWithComponentsEssentiality => {
  const appsData = components.reduce((acc, compRef) => {
    const { isEssential, appData, essentialChildren } =
      shouldClassifyAsEssential(documentServices, experimentsAPI, compRef);

    const appDefId = appData?.appDefinitionId ?? 'DEFAULTS';
    addAppKeyIfNeeded(acc, appDefId, appData);
    if (isEssential) {
      acc[appDefId].essentialsComponents.push(compRef);
    } else {
      if (essentialChildren && essentialChildren.length > 0) {
        essentialChildren?.forEach((child) => {
          const childAppDefId: string = child.appData.appDefinitionId;
          addAppKeyIfNeeded(acc, childAppDefId, child.appData);
          acc[childAppDefId].essentialsComponents.push(child.compRef);
        });
      }
      acc[appDefId].nonEssentialsComponents.push(compRef);
    }
    return acc;
  }, {} as AppDataWithComponentsEssentiality);

  return appsData;
};

export const handleEssentialsComponentsBeforeDelete = async (
  context: PlatformContext,
  documentServices: DocumentServicesObject,
  components: CompRef[],
): Promise<CompRef[]> => {
  let compRefsToDelete: CompRef[] = [];

  const appsData = getComponentsAppsDataWithEssentiality(
    documentServices,
    context.experimentsAPI,
    components,
  );

  for (const appData of Object.values(appsData)) {
    if (appData.essentialsComponents.length > 0) {
      const compRef = appData.essentialsComponents[0];
      await context.platformApiMethods.document.application.uninstall(
        appData,
        'TOKEN',
        { openConfirmation: true, compRef },
      );
    } else {
      compRefsToDelete = [
        ...compRefsToDelete,
        ...appData.nonEssentialsComponents,
      ];
    }
  }

  return compRefsToDelete;
};

export const getAppDataByComponentId = (
  context: ExtendedPlatformContext,
  componentId: string,
): AppData | null => {
  const installedApps = context.documentServices.platform.getInstalledApps();

  const appDefId = installedApps.find((app) =>
    app.components?.find((component) => component.componentId === componentId),
  )?.appDefinitionId;

  if (!appDefId) {
    return null;
  }

  return context.documentServices.platform.getAppDataByAppDefId(
    appDefId,
  ) as AppData;
};

export const addOOIComponent = async (
  context: ExtendedPlatformContext,
  appData: AppData,
  componentType: string,
  containerRef: CompRef,
) => {
  const component = appData?.components?.find(
    (comp) =>
      comp.type === ComponentType.WIDGET_OUT_OF_IFRAME &&
      // @ts-expect-error - add componentType in DM types
      (comp.data?.componentType === componentType ||
        comp.componentId === componentType),
  );
  if (!component) {
    throw new Error(
      `Failed to find component for component type/id ${componentType}`,
    );
  }

  const tpaWidgetId =
    // @ts-expect-error - add widgetData in DM types
    component.data?.widgetData?.tpaWidgetId ??
    // @ts-expect-error - add widgetData in DM types
    component.data?.widgetData?.widgetId ??
    component.componentId;
  if (!tpaWidgetId) {
    throw new Error('Failed to find tpa/widgetId for component');
  }

  let addResult;
  try {
    addResult = await context.platformApiMethods.document.tpa.add.component(
      appData,
      'TOKEN',
      {
        componentType: TPAComponentType.Widget,
        pageId: containerRef.id,
        appDefinitionId: appData?.appDefinitionId,
        widget: {
          widgetId: tpaWidgetId,
        },
      },
    );
  } catch (error) {
    throw new Error('Failed to add OOI component', { cause: error });
  }

  const compRef = {
    id: addResult.compId,
    type: 'DESKTOP' as PossibleViewModes,
  };
  const editorPointer =
    context.editorPlatformPackagesBridge.EditorPointerAPI?.buildEditorPointer(
      compRef,
    );
  if (!editorPointer) {
    throw new Error('Failed to build editor pointer');
  }

  const { initialSize, presets } =
    context.componentsPlatformService.components.getInstallation(
      editorPointer,
    ) ?? {};

  // TODO: do we need to resize or Editor handles it and we only need to apply presets?
  if (initialSize) {
    const parentPointer =
      context.editorPlatformPackagesBridge.EditorPointerAPI?.buildEditorPointer(
        context.documentServices.components.getContainer(compRef),
      );

    try {
      context.platformApiMethods.editor.updateComponentLayout?.({
        editorPointer,
        targetSize: initialSize,
        parentPointer,
      });
    } catch (error) {
      console.error('Failed to update component layout', error);
    }
  }

  if (presets) {
    try {
      applyPresets(context, compRef);
    } catch (error) {
      console.error('Failed to apply installation presets', compRef, error);
    }
  }

  return compRef;
};

export const addCustomElementComponent = async (
  context: ExtendedPlatformContext,
  appData: AppData,
  customElementComponent: AppComponent,
  containerRef: CompRef,
) => {
  const { data, componentId, type } = customElementComponent;
  if (!data) {
    throw new Error('Custom element component data not found');
  }

  const widgetDefinition = {
    ...data,
    type,
    widgetId: componentId,
  } as UnifiedWidget;

  try {
    const compRef = await addCustomElement(
      context,
      appData,
      widgetDefinition,
      containerRef,
      {},
    );

    return compRef;
  } catch (error) {
    throw new Error('Failed to add custom element component', { cause: error });
  }
};

export const getManifestTypeForBuilderComponentType = (
  context: ExtendedPlatformContext,
  componentType: string,
): ManifestComponentExtensionType => {
  const isTpaComponent =
    context.documentServices.tpa.isTpaByCompType(componentType);

  if (isTpaComponent) {
    // For TPA components, check if it's a widget (OOI widget)
    const isTpaWidget =
      context.documentServices.tpa.isTpaWidgetByCompType(componentType);
    if (isTpaWidget) {
      return ManifestComponentExtensionType.OOI;
    }
    // TPA component that is not a widget (e.g., OOI page) - not addable
    return ManifestComponentExtensionType.UNKNOWN;
  }

  // Not a TPA component, check if it's a Builder component (not CustomElement)
  const isCustomElement = componentType === ComponentTypes.CustomElement;
  if (!isCustomElement) {
    return ManifestComponentExtensionType.BUILDER;
  }

  // CustomElement - not addable via this path
  return ManifestComponentExtensionType.UNKNOWN;
};

export const getManifestTypeFromAppData = (
  appData: AppData,
  componentType: string,
): ManifestComponentExtensionType => {
  const component = appData.components?.find(
    (comp) => comp.componentId === componentType,
  );

  if (!component) {
    return ManifestComponentExtensionType.UNKNOWN;
  }

  const extensionType = component.type;
  switch (extensionType) {
    case ComponentType.WIDGET_OUT_OF_IFRAME:
      return ManifestComponentExtensionType.OOI;
    case ComponentType.EDITOR_REACT_COMPONENT:
      return ManifestComponentExtensionType.BUILDER;
    case ComponentType.CUSTOM_ELEMENT_WIDGET:
      return ManifestComponentExtensionType.CUSTOM_ELEMENT;
    default:
      return ManifestComponentExtensionType.UNKNOWN;
  }
};

export const mapComponentTypeToManifestType = (
  context: ExtendedPlatformContext,
  appData: AppData,
  componentType: string,
): ManifestComponentExtensionType => {
  const isBuilderComponentType =
    context.componentsPlatformService.applicationData.isBuilderComponentType(
      componentType,
    );

  if (isBuilderComponentType) {
    // isBuilderComponentType returns true for:
    // - OOI component types (page, widget, etc.)
    // - Builder component types
    // - CustomElement shared type (wixui.CustomElementComponent)
    return getManifestTypeForBuilderComponentType(context, componentType);
  }

  // Component type is not a builder component type, check if it's an extension ID
  return getManifestTypeFromAppData(appData, componentType);
};

export const getBuilderComponentDefinition = (
  context: ExtendedPlatformContext,
  componentType: string,
  appData: Required<AppData>,
): BuilderComponent | null => {
  return (
    ((context.componentsPlatformService.applicationData.getAppComponentDataByComponentType(
      componentType,
    ) ??
      appData.components.find(
        (comp) => comp.componentId === componentType,
      )) as BuilderComponent) ?? null
  );
};

export const addBuilderComponentByType = async (
  context: ExtendedPlatformContext,
  componentType: string,
  containerPointer: EditorPointer<CompRef>,
  options?: {
    preset?: string;
  },
): Promise<EditorPointer<CompRef> | null> => {
  return context.documentServices.transactions.run(async () => {
    const containerRef =
      context.editorPlatformPackagesBridge.EditorPointerAPI?.getDocumentPointer<CompRef>(
        containerPointer,
      );
    if (!containerRef) {
      throw new Error('Invalid container pointer');
    }

    const appData =
      (context.componentsPlatformService.applicationData.getAppDataByComponentType(
        componentType,
      ) as Required<AppData>) ??
      getAppDataByComponentId(context, componentType);
    if (!appData?.appDefinitionId) {
      throw new Error('Invalid component type - no appDefId found');
    }

    const manifestType = mapComponentTypeToManifestType(
      context,
      appData,
      componentType,
    );

    let compRef;
    switch (manifestType) {
      case ManifestComponentExtensionType.OOI:
        compRef = await addOOIComponent(
          context,
          appData,
          componentType,
          containerRef,
        );
        break;
      case ManifestComponentExtensionType.BUILDER:
        const builderComponent = getBuilderComponentDefinition(
          context,
          componentType,
          appData,
        );

        if (!builderComponent) {
          throw new Error(
            'Invalid component type - no builder component found',
          );
        }
        try {
          compRef = await _addBuilderComponent(
            context,
            appData,
            builderComponent,
            containerRef,
          );
        } catch (error) {
          throw new Error('Failed to add builder component');
        }
        break;
      case ManifestComponentExtensionType.CUSTOM_ELEMENT:
        const customElementComponent = appData.components?.find(
          (comp) =>
            comp.type === ComponentType.CUSTOM_ELEMENT_WIDGET &&
            comp.componentId === componentType,
        ) as AppComponent;

        if (!customElementComponent) {
          throw new Error(
            'Invalid component type - no custom element component found',
          );
        }

        compRef = await addCustomElementComponent(
          context,
          appData,
          customElementComponent,
          containerRef,
        );
        break;
      case ManifestComponentExtensionType.UNKNOWN:
      default:
        const errorMessage =
          'Invalid component type - no supported extension type found';
        console.error(errorMessage, { manifestType, componentType, appData });
        throw new Error(errorMessage);
    }

    const compPointer =
      context.editorPlatformPackagesBridge.EditorPointerAPI?.buildEditorPointer<CompRef>(
        compRef,
      );
    if (!compPointer) {
      throw new Error('Failed to build component pointer');
    }

    if (
      manifestType !== ManifestComponentExtensionType.CUSTOM_ELEMENT &&
      options?.preset
    ) {
      const presets =
        context.componentsPlatformService.components.getPresets(compPointer);
      if (!presets?.[options.preset]) {
        console.log('invalid preset override', options.preset, presets);
        return compPointer;
      }

      try {
        context.componentsPlatformService.presets.applyPreset(
          compPointer,
          options.preset,
        );
      } catch (error) {
        throw new Error('Failed to apply preset', { cause: error });
      }
    }

    return compPointer;
  });
};

export const addBuilderComponent = (
  context: ExtendedPlatformContext,
  builderComponent: BuilderComponent,
  containerRef: CompRef,
): Promise<CompRef> => {
  const compAppData = getCompAppData(
    context.documentServices,
    context.experimentsAPI,
    containerRef,
  );

  if (!compAppData?.appData) {
    throw new Error('Component app data not found');
  }

  return _addBuilderComponent(
    context,
    compAppData.appData,
    builderComponent,
    containerRef,
  );
};
